<template>
  <div>
    <Navbar />

    <main>

      <!-- ── Page hero ── -->
      <section class="page-hero">
        <div class="page-hero-overlay"></div>
        <div class="page-container">
          <div class="page-hero-content">
            <div class="hero-badge">
              <font-awesome-icon icon="tag" class="me-1" /> Transparent Pricing
            </div>
            <h1 class="page-hero-title">Simple, Honest Pricing</h1>
            <p class="page-hero-sub">No hidden fees. No surprises. Pick the plan that works for your business.</p>
            <nav class="breadcrumb-nav" aria-label="Breadcrumb">
              <router-link to="/home" class="breadcrumb-link">Home</router-link>
              <span class="breadcrumb-sep">/</span>
              <span class="breadcrumb-current">Pricing</span>
            </nav>
          </div>
        </div>
      </section>

      <!-- ── Pricing plans — commented out; replaced with quote form ── -->
      <!--
      <section class="section-pad" id="pricing">
        <div class="page-container">

          <div class="section-header">
            <h2 class="section-title">Our Pricing Plans</h2>
            <p class="section-body">Get started with a fully integrated management system. We have the ideal package for your business.</p>
          </div>

          <div v-if="plansLoading" class="plans-loading">
            <div class="spinner-border text-primary" style="width:2.5rem;height:2.5rem;" role="status"></div>
            <p class="plans-loading-text">Loading plans...</p>
          </div>

          <template v-else>

            <div class="billing-tabs">
              <button
                v-for="cycle in billingCycles"
                :key="cycle.key"
                class="billing-tab"
                :class="{ 'billing-tab--active': activeCycle === cycle.key }"
                @click="activeCycle = cycle.key"
              >
                {{ cycle.label }}
                <span v-if="cycle.saving" class="saving-badge">{{ cycle.saving }}</span>
              </button>
            </div>

            <div v-if="!activePlans.length" class="plans-empty">
              <font-awesome-icon icon="box-open" class="plans-empty-icon" />
              <p>No plans available for this billing cycle.</p>
            </div>

            <div v-else class="plans-grid">
              <div
                v-for="plan in activePlans"
                :key="plan.id"
                class="plan-card"
                :class="{ 'plan-card--popular': plan.is_popular }"
              >
                <div v-if="plan.is_popular" class="popular-badge">⭐ Most Popular</div>

                <div class="plan-header">
                  <div class="plan-name">{{ plan.name }}</div>
                  <div class="plan-price-wrap">
                    <span class="plan-currency">{{ currencySymbol }}</span>
                    <span class="plan-amount">{{ formatCurrency(plan.amount) }}</span>
                  </div>
                  <div class="plan-cycle">per {{ plan.type }}</div>
                </div>

                <div class="plan-divider"></div>

                <ul class="plan-features">
                  <li v-for="(desc, i) in planFeatures(plan)" :key="i">
                    <font-awesome-icon icon="check-circle" class="plan-check" />
                    <span>{{ desc }}</span>
                  </li>
                </ul>

                <button
                  class="plan-cta"
                  :class="{ 'plan-cta--popular': plan.is_popular }"
                  @click="paymentModal(plan)"
                >
                  Get Started
                  <font-awesome-icon icon="arrow-right" class="ms-1" />
                </button>
              </div>
            </div>

          </template>
        </div>
      </section>
      -->

      <!-- ── Quote Request Form ── -->
      <section class="section-pad" id="pricing">
        <div class="page-container">
          <div class="quote-wrap">

            <!-- Left: copy -->
            <div class="quote-copy">
              <p class="section-eyebrow">Custom Pricing</p>
              <h2 class="section-title">Let Us Build the Right Plan for You</h2>
              <p class="section-body">
                We don't do one-size-fits-all. Share a few details about your business and we'll assess your operations to recommend the exact module setup — and the pricing that fits.
              </p>
              <ul class="quote-perks">
                <li><font-awesome-icon icon="check-circle" class="perk-icon" /> Free, no-obligation assessment</li>
                <li><font-awesome-icon icon="check-circle" class="perk-icon" /> Tailored module recommendations</li>
                <li><font-awesome-icon icon="check-circle" class="perk-icon" /> Transparent, competitive pricing</li>
                <li><font-awesome-icon icon="check-circle" class="perk-icon" /> Response within 24 hours</li>
              </ul>
            </div>

            <!-- Right: form -->
            <div class="quote-form-card">

              <!-- Success state -->
              <div v-if="quoteSent" class="quote-success">
                <div class="quote-success-icon"><font-awesome-icon icon="check-circle" /></div>
                <h3 class="quote-success-title">Request Received!</h3>
                <p class="quote-success-body">Thank you! We'll review your requirements and get back to you with a tailored proposal shortly.</p>
              </div>

              <!-- Form -->
              <template v-else>
                <h3 class="form-card-title">Request a Quote</h3>
                <p v-if="quoteError" class="form-error">{{ quoteError }}</p>

                <div class="form-row-2">
                  <div class="form-group">
                    <label class="form-label">Your Name <span class="req">*</span></label>
                    <input v-model="quoteForm.name" class="form-input" placeholder="John Doe" />
                  </div>
                  <div class="form-group">
                    <label class="form-label">Email Address <span class="req">*</span></label>
                    <input v-model="quoteForm.email" type="email" class="form-input" placeholder="john@company.com" />
                  </div>
                </div>

                <div class="form-row-2">
                  <div class="form-group">
                    <label class="form-label">Phone Number <span class="req">*</span></label>
                    <input v-model="quoteForm.phone" class="form-input" placeholder="+234 800 000 0000" />
                  </div>
                  <div class="form-group">
                    <label class="form-label">Company Name <span class="req">*</span></label>
                    <input v-model="quoteForm.company_name" class="form-input" placeholder="Acme Ltd" />
                  </div>
                </div>

                <div class="form-group">
                  <label class="form-label">Number of Employees</label>
                  <select v-model="quoteForm.company_size" class="form-input">
                    <option value="">Select range...</option>
                    <option value="1–10">1 – 10</option>
                    <option value="11–50">11 – 50</option>
                    <option value="51–200">51 – 200</option>
                    <option value="201–500">201 – 500</option>
                    <option value="500+">500+</option>
                  </select>
                </div>

                <div class="form-group">
                  <label class="form-label">Modules You're Interested In</label>
                  <div class="modules-grid">
                    <label v-for="m in moduleOptions" :key="m.value" class="module-check">
                      <input type="checkbox" :value="m.value" v-model="quoteForm.modules" />
                      <span>{{ m.label }}</span>
                    </label>
                  </div>
                </div>

                <div class="form-group">
                  <label class="form-label">Additional Notes</label>
                  <textarea v-model="quoteForm.message" class="form-input" rows="4" placeholder="Tell us more about your business needs, current challenges, or anything else that would help us understand your setup..."></textarea>
                </div>

                <button class="btn-quote-submit" @click="submitQuote" :disabled="quoteLoading">
                  <span v-if="quoteLoading">Submitting...</span>
                  <span v-else>Submit Quote Request <font-awesome-icon icon="arrow-right" class="ms-1" /></span>
                </button>
              </template>
            </div>
          </div>
        </div>
      </section>

      <!-- ── FAQ strip ── -->
      <section class="section-pad section-gray">
        <div class="page-container faq-strip">
          <div v-for="faq in faqs" :key="faq.q" class="faq-item">
            <div class="faq-q">
              <font-awesome-icon icon="question-circle" class="faq-icon" />
              {{ faq.q }}
            </div>
            <div class="faq-a">{{ faq.a }}</div>
          </div>
        </div>
      </section>

      <!-- ── CTA ── -->
      <section class="cta-band">
        <div class="page-container cta-band-inner">
          <div>
            <h2 class="cta-title">Need help choosing a plan?</h2>
            <p class="cta-sub">Our experts can guide you to the best Mapiibs plan for your company size and workflow.</p>
          </div>
          <router-link to="/contact" class="btn-white-lg">
            Talk To Us <font-awesome-icon icon="arrow-right" class="ms-2" />
          </router-link>
        </div>
      </section>

    </main>

    <Footer />

    <!-- ── Pricing Modals (login + payment) — from pricingMixin ── -->
    <PricingModals
      :loginModalOpen="loginModalOpen"
      :paymentModalOpen="paymentModalOpen"
      :currentData="currentData"
      :login="login"
      :validationErrors="validationErrors"
      :loginLoading="loginLoading"
      :countryCode="countryCode"
      :currencySymbol="currencySymbol"
      :localPaymentChannels="localPaymentChannels"
      :internationalPaymentChannels="internationalPaymentChannels"
      :backendUrl="backend_url.hostName"
      @close-login="loginModalOpen = false"
      @close-payment="paymentModalOpen = false"
      @update:login="login = $event"
      @login="loginUser"
      @flutterwave="makeFlutterwavePayment($event.data, $event.channel)"
      @paystack="makePaystackPayment($event.data, $event.channel)"
      @stripe="payWithStripe($event.data, $event.channel)"
    />

  </div>
</template>

<script>
import Navbar        from '../components/Navbar';
import Footer        from '../components/footer';
import PricingModals from '@/components/PricingModals.vue';
import { pricingMixin }  from '@/lib/PricingMixin';
import { reusableMixin } from '../lib/reusableMixin';
import axios from 'axios';

export default {
  name: 'PricingPage',

  components: { Navbar, Footer, PricingModals },

  mixins: [pricingMixin, reusableMixin],

  data() {
    return {
      plansLoading: true,

      // Billing cycles — saving badges shown on longer cycles
      billingCycles: [
        { key: 'monthly',   label: 'Monthly',   saving: null       },
        { key: 'quarterly', label: 'Quarterly',  saving: 'Save 10%' },
        { key: 'halfyear',  label: 'Half Year',  saving: 'Save 15%' },
        { key: 'yearly',    label: 'Yearly',     saving: 'Save 25%' },
      ],

      faqs: [
        { q: 'Can I change my plan later?',     a: 'Yes — you can upgrade or downgrade at any time. Changes take effect at the start of your next billing cycle.' },
        { q: 'Is there a free trial?',          a: 'All new accounts start on a free trial period. No credit card required to get started.' },
        { q: 'What payment methods do you accept?', a: 'We accept card payments via Paystack and Flutterwave (Nigeria) and Stripe (international).' },
        { q: 'Can I cancel anytime?',           a: 'Absolutely. There are no long-term contracts. Cancel directly from your account settings.' },
      ],

      // Quote request form
      quoteSent:    false,
      quoteLoading: false,
      quoteError:   '',
      quoteForm: {
        name:         '',
        email:        '',
        phone:        '',
        company_name: '',
        company_size: '',
        modules:      [],
        message:      '',
      },
      moduleOptions: [
        { value: 'HR & Payroll',          label: 'HR & Payroll' },
        { value: 'Inventory',             label: 'Inventory' },
        { value: 'Accounting',            label: 'Accounting' },
        { value: 'CRM',                   label: 'CRM' },
        { value: 'Procurement',           label: 'Procurement' },
        { value: 'Project Management',    label: 'Project Management' },
        { value: 'Training',              label: 'Training' },
        { value: 'Asset Management',      label: 'Asset Management' },
        { value: 'Visitor Management',    label: 'Visitor Management' },
        { value: 'Invoicing & Billing',   label: 'Invoicing & Billing' },
      ],
    };
  },

  async mounted() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    await this.initPricing();
    this.plansLoading = false;
    if (this.superUser) this.superUser();
  },

  methods: {
    async submitQuote() {
      this.quoteError = '';
      const f = this.quoteForm;
      if (!f.name || !f.email || !f.phone || !f.company_name) {
        this.quoteError = 'Please fill in all required fields.';
        return;
      }
      this.quoteLoading = true;
      try {
        await axios.post('request-quote', f);
        this.quoteSent = true;
      } catch (e) {
        this.quoteError = e.response?.data?.message ?? 'Submission failed. Please try again.';
      } finally {
        this.quoteLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Shared ──────────────────────────────────────────────────────────── */
.page-container { max-width:1160px; margin:0 auto; padding:0 24px; }
.section-pad    { padding:80px 0; }
.section-gray   { background:#f4f7fb; }
.me-1 { margin-right:5px; }
.ms-1 { margin-left:5px; }
.ms-2 { margin-left:8px; }

.section-header { text-align:center; max-width:680px; margin:0 auto 52px; }
.section-title  { font-size:clamp(24px,3.5vw,34px); font-weight:700; color:#1a2b45; margin:0 0 14px; }
.section-body   { font-size:15px; color:#6b7a99; line-height:1.7; margin:0; }

/* ── Page hero ───────────────────────────────────────────────────────── */
.page-hero {
  position: relative;
  background: linear-gradient(135deg, #051B35 0%, #004AAD 100%);
  padding: 100px 0 64px;
  overflow: hidden;
  text-align: center;
}
.page-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background: url('/static/img/bg-1.jpg') center/cover no-repeat;
  opacity: 0.06;
}
.page-hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(5,27,53,.94) 0%, rgba(0,74,173,.78) 100%);
}
.page-hero-content  { position:relative; z-index:1; }

.hero-badge {
  display: inline-flex; align-items:center; gap:6px;
  background: rgba(255,255,255,.1); border:1px solid rgba(255,255,255,.18);
  color: rgba(255,255,255,.9); border-radius:20px;
  padding: 4px 14px; font-size:12px; font-weight:700;
  text-transform:uppercase; letter-spacing:.07em; margin-bottom:18px;
}
.page-hero-title  { font-size:clamp(30px,6vw,50px); font-weight:800; color:#fff; margin:0 0 14px; }
.page-hero-sub    { font-size:16px; color:rgba(255,255,255,.72); margin:0 0 24px; max-width:520px; margin-left:auto; margin-right:auto; line-height:1.6; }
.breadcrumb-nav   { display:inline-flex; align-items:center; gap:8px; font-size:14px; }
.breadcrumb-link  { color:rgba(255,255,255,.65); text-decoration:none; transition:color .15s; }
.breadcrumb-link:hover { color:#fff; }
.breadcrumb-sep   { color:rgba(255,255,255,.3); }
.breadcrumb-current { color:rgba(255,255,255,.45); }

/* ── Loading / empty ─────────────────────────────────────────────────── */
.plans-loading      { display:flex; flex-direction:column; align-items:center; gap:14px; padding:60px 0; }
.plans-loading-text { font-size:14px; color:#7a90b0; margin:0; }
.plans-empty        { display:flex; flex-direction:column; align-items:center; gap:12px; padding:60px 0; text-align:center; color:#7a90b0; font-size:14px; }
.plans-empty-icon   { font-size:36px; opacity:.3; }

/* ── Billing tabs ────────────────────────────────────────────────────── */
.billing-tabs {
  display: flex;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 52px;
  background: #f4f7fb;
  border: 1px solid #e4eaf2;
  border-radius: 12px;
  padding: 6px;
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
}

.billing-tab {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  border-radius: 8px;
  border: none;
  background: transparent;
  font-size: 13.5px;
  font-weight: 600;
  color: #7a90b0;
  cursor: pointer;
  transition: all .18s;
  white-space: nowrap;
  font-family: inherit;
}
.billing-tab:hover { color:#1a2b45; background:rgba(0,0,0,.04); }
.billing-tab--active { background:#fff; color:#004AAD; box-shadow:0 2px 8px rgba(0,0,0,.1); }

.saving-badge {
  background: #16a34a;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  border-radius: 10px;
  padding: 2px 7px;
  white-space: nowrap;
}

/* ── Plans grid ──────────────────────────────────────────────────────── */
.plans-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  align-items: start;
}

/* ── Plan card ───────────────────────────────────────────────────────── */
.plan-card {
  position: relative;
  background: #fff;
  border: 1px solid #e4eaf2;
  border-radius: 16px;
  padding: 32px 28px;
  display: flex;
  flex-direction: column;
  gap: 0;
  transition: box-shadow .2s, transform .2s, border-color .2s;
}
.plan-card:hover {
  box-shadow: 0 16px 40px rgba(0,0,0,.1);
  transform: translateY(-4px);
}
.plan-card--popular {
  border-color: #004AAD;
  border-width: 2px;
  box-shadow: 0 8px 32px rgba(0,74,173,.15);
}

.popular-badge {
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  background: #004AAD;
  color: #fff;
  font-size: 11.5px;
  font-weight: 700;
  border-radius: 20px;
  padding: 4px 14px;
  white-space: nowrap;
}

/* ── Plan header ─────────────────────────────────────────────────────── */
.plan-header  { text-align:center; padding-top:10px; margin-bottom:24px; }
.plan-name    { font-size:15px; font-weight:700; color:#7a90b0; text-transform:uppercase; letter-spacing:.08em; margin-bottom:14px; }
.plan-price-wrap { display:flex; align-items:flex-start; justify-content:center; gap:2px; }
.plan-currency   { font-size:20px; font-weight:700; color:#1a2b45; margin-top:8px; }
.plan-amount     { font-size:48px; font-weight:800; color:#1a2b45; line-height:1; }
.plan-cycle      { font-size:12.5px; color:#7a90b0; font-weight:600; text-transform:uppercase; letter-spacing:.05em; margin-top:6px; }

/* ── Plan divider ────────────────────────────────────────────────────── */
.plan-divider { height:1px; background:#f0f4f8; margin:0 0 20px; }

/* ── Plan features ───────────────────────────────────────────────────── */
.plan-features {
  list-style: none;
  padding: 0;
  margin: 0 0 28px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 11px;
}
.plan-features li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13.5px;
  color: #3d5068;
  line-height: 1.45;
}
.plan-check { color:#16a34a; font-size:13px; flex-shrink:0; margin-top:1px; }

/* ── CTA button ──────────────────────────────────────────────────────── */
.plan-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  width: 100%;
  padding: 12px;
  border-radius: 10px;
  border: 2px solid #004AAD;
  background: transparent;
  color: #004AAD;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all .15s;
  font-family: inherit;
}
.plan-cta:hover { background:#004AAD; color:#fff; }
.plan-cta--popular { background:#004AAD; color:#fff; }
.plan-cta--popular:hover { background:#003b8e; border-color:#003b8e; }

/* ── FAQ strip ───────────────────────────────────────────────────────── */
.faq-strip { display:grid; grid-template-columns:repeat(2,1fr); gap:24px; }
.faq-item  { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:22px 24px; }
.faq-q     { display:flex; align-items:flex-start; gap:10px; font-size:14.5px; font-weight:700; color:#1a2b45; margin-bottom:10px; }
.faq-icon  { color:#004AAD; font-size:15px; flex-shrink:0; margin-top:1px; }
.faq-a     { font-size:13.5px; color:#6b7a99; line-height:1.65; }

/* ── CTA band ────────────────────────────────────────────────────────── */
.cta-band       { background:linear-gradient(135deg,#004AAD,#0059d6); padding:56px 0; }
.cta-band-inner { display:flex; justify-content:space-between; align-items:center; gap:24px; flex-wrap:wrap; }
.cta-title      { font-size:26px; font-weight:700; color:#fff; margin:0 0 8px; }
.cta-sub        { font-size:15px; color:rgba(255,255,255,.75); margin:0; }
.btn-white-lg {
  display: inline-flex; align-items:center; gap:8px;
  background: #fff; color:#004AAD;
  padding: 12px 26px; border-radius:10px;
  font-size: 14px; font-weight:700;
  text-decoration: none; transition:all .15s; white-space:nowrap;
}
.btn-white-lg:hover { background:#e8f0fb; text-decoration:none; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width:960px)  { .plans-grid { grid-template-columns:repeat(2,1fr); } .faq-strip { grid-template-columns:1fr; } }
@media (max-width:640px)  {
  .plans-grid     { grid-template-columns:1fr; }
  .section-pad    { padding:56px 0; }
  .page-hero      { padding:80px 0 52px; }
  .cta-band-inner { flex-direction:column; align-items:flex-start; }
  .billing-tabs   { width:100%; }
}

/* ── Quote request section ───────────────────────────────────────────────── */
.quote-wrap {
  display: grid;
  grid-template-columns: 1fr 1.1fr;
  gap: 56px;
  align-items: start;
}

.section-eyebrow {
  font-size: 12px; font-weight: 700; letter-spacing: .1em;
  text-transform: uppercase; color: #004AAD; margin: 0 0 10px;
}
.quote-perks {
  list-style: none; padding: 0; margin: 24px 0 0;
  display: flex; flex-direction: column; gap: 12px;
}
.quote-perks li {
  display: flex; align-items: center; gap: 10px;
  font-size: 14.5px; color: #3d5068;
}
.perk-icon { color: #16a34a; font-size: 15px; flex-shrink: 0; }

.quote-form-card {
  background: #fff;
  border: 1px solid #e4eaf2;
  border-radius: 18px;
  padding: 36px 32px;
  box-shadow: 0 8px 32px rgba(0,0,0,.06);
}
.form-card-title { font-size: 20px; font-weight: 700; color: #1a2b45; margin: 0 0 22px; }

.form-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
.form-group { margin-bottom: 16px; }
.form-label { display: block; font-size: 13px; font-weight: 600; color: #1a2b45; margin-bottom: 6px; }
.req { color: #e53e3e; }
.form-input {
  width: 100%; padding: 10px 14px;
  border: 1px solid #d0daea; border-radius: 9px;
  font-size: 14px; color: #1a2b45; font-family: inherit;
  background: #fff; transition: border-color .15s; box-sizing: border-box;
}
.form-input:focus { outline: none; border-color: #004AAD; }
textarea.form-input { resize: vertical; }
.form-error { color: #e53e3e; font-size: 13px; margin: 0 0 14px; }

.modules-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px 12px;
  margin-top: 6px;
}
.module-check {
  display: flex; align-items: center; gap: 8px;
  font-size: 13.5px; color: #3d5068; cursor: pointer;
}
.module-check input[type="checkbox"] { accent-color: #004AAD; width: 15px; height: 15px; flex-shrink: 0; }

.btn-quote-submit {
  width: 100%; padding: 13px;
  background: #004AAD; color: #fff;
  border: none; border-radius: 10px;
  font-size: 15px; font-weight: 700; font-family: inherit;
  cursor: pointer; transition: background .15s;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  margin-top: 8px;
}
.btn-quote-submit:hover:not(:disabled) { background: #003b8e; }
.btn-quote-submit:disabled { opacity: .65; cursor: not-allowed; }

.quote-success {
  text-align: center; padding: 40px 20px;
}
.quote-success-icon { font-size: 52px; color: #16a34a; margin-bottom: 18px; }
.quote-success-title { font-size: 22px; font-weight: 700; color: #1a2b45; margin: 0 0 12px; }
.quote-success-body  { font-size: 15px; color: #6b7a99; line-height: 1.7; margin: 0; }

@media (max-width: 860px) {
  .quote-wrap    { grid-template-columns: 1fr; gap: 32px; }
  .form-row-2    { grid-template-columns: 1fr; }
  .modules-grid  { grid-template-columns: 1fr; }
  .quote-form-card { padding: 24px 20px; }
}
</style>